import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Paper,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { debounce } from 'lodash';

const FilterToolbar = ({ filters, onFilterChange, statusOptions = [] }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [expanded, setExpanded] = useState(false);

  const debouncedFilterChange = useMemo(() => debounce(onFilterChange, 300), [onFilterChange]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    debouncedFilterChange(localFilters);
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [localFilters, debouncedFilterChange]);


  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (event) => {
    const {
      target: { value },
    } = event;
    setLocalFilters(prev => ({
      ...prev,
      statuses: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleClear = () => {
    const clearedFilters = {
      q: '',
      startDate: '',
      endDate: '',
      statuses: [],
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters); // Notify parent immediately
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Buscar..."
          name="q"
          value={localFilters.q || ''}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ flexGrow: 1 }}
        />
        <Tooltip title="Filtros Avanzados">
          <IconButton onClick={toggleExpanded}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Desde"
                name="startDate"
                type="date"
                value={localFilters.startDate || ''}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Hasta"
                name="endDate"
                type="date"
                value={localFilters.endDate || ''}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {statusOptions.length > 0 && (
              <Grid item xs={12} sm={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Estado</InputLabel>
                  <Select
                    labelId="status-select-label"
                    multiple
                    name="statuses"
                    value={localFilters.statuses || []}
                    onChange={handleStatusChange}
                    input={<OutlinedInput label="Estado" />}
                    renderValue={(selected) => {
                      // Find the labels for the selected values
                      const selectedLabels = selected
                        .map(value => {
                          const option = statusOptions.find(opt => opt.value === value);
                          return option ? option.label : value;
                        })
                        .join(', ');
                      return selectedLabels;
                    }}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        <Checkbox checked={(localFilters.statuses || []).indexOf(status.value) > -1} />
                        <ListItemText primary={status.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default FilterToolbar;
